import commonUtils from "../utils/common.utils"
import orderContentModel from "./orderContent.model"

//---- 線上點餐
// (LA) 0: 門市內用
// (LB) 1: 現場外帶
// (LC) 2: 預約自取
// (LD) 3: 店家外送
//---- 線上零售
// (LE) 4: 零售自取
// (LF) 5: 零售宅配

const consumptionPatternMap = {
    0: '門市內用',
    1: '現場外帶',
    2: '預約自取',
    3: '店家外送',
    4: '零售自取',
    5: '零售宅配'
}

export default class orderHistoryModel {
    constructor(orderHistory) {
        this._orderHistory = orderHistory
        // 消費模式
        this.consumptionPattern = orderHistory.consumption_pattern
        // 消費金額
        this.orderTotalPrice = orderHistory?.order_amount?.toLocaleString() || 0
        // 訂單結帳時間 || 訂單取餐時間
        this.orderDate = commonUtils.convertDate(orderHistory.created_at) || commonUtils.convertDate(orderHistory.order_checkout_time)
        this.orderDateTime = commonUtils.convertDateTime(orderHistory.created_at) || commonUtils.convertDateTime(orderHistory.order_checkout_time)
        // 訂單建立時間
        this.orderStagedTime = orderHistory?.staged_time ? commonUtils.convertDateTime(orderHistory.staged_time) : ''
        // 訂單內容
        this.orderContent = new orderContentModel(JSON.parse(orderHistory.order_content))
        // 訂單狀態
        // 0: 未處理
        // 1: 使用者取消
        // 2: 處理中
        // 3: 已結帳
        // 4: 店家取消訂單
        this.orderStatus = orderHistory.order_status
        // 商店代號
        this.storeCode = orderHistory.store_code
        // 商店會員編號
        this.storeMemberId = orderHistory.store_member_id
        // 商店會員UUID
        this.storeMemberUuid = orderHistory.store_member_uuid
        // 訂單編號
        this.orderNumber = orderHistory.order_number
    }

    // 結帳 or 下單時間文字
    // 先判斷結帳 再判斷下單
    get checkOutTimeText() {
        if (this._orderHistory.order_checkout_time) {
            return '結帳'
        }
        return '下單'
    }

    // 結帳 or 下單時間
    get checkOutTime() {
        if (this._orderHistory.order_checkout_time) {
            return commonUtils.convertDateTime(this._orderHistory.order_checkout_time)
        }
        return commonUtils.convertDateTime(this._orderHistory.created_at)
    }

    // 訂單取餐/送達時間
    get takeOrderTime() {
        // 預約/零售自取(2/4) 或 店家外送/零售宅配(3/5) 才顯示時間
        if (this.isPickupType || this.isDeliveryType) {
            if (this._orderHistory.take_order_time) {
                return commonUtils.convertDateTime(this._orderHistory.take_order_time)
            }
            if (this.orderContent.deliverTime) {
                return commonUtils.convertDateTime(this.orderContent.deliverTime)
            }
            return ''
        }
        return ''
    }

    get orderNumberTrailing() {
        return this.displayOrderNumber.slice(-3) || ''
    }

    get displayTotalPrice() {
        // 如果是子單 價格強制顯示為0
        if (this.isChildOrder) return 0
        let total = this.orderContent.totalPrice
        if (this.orderContent.hasAdditionalFee) {
            // 紅利積點
            total -= Math.abs(this.orderContent.pointDiscount)
            // 現場減價
            total -= Math.abs(this.orderContent.onSiteDiscount)
            // 現場折價 
            total -= this.orderContent.discountPrice
            // 額外收費
            total += this.orderContent.extraFee
            // 服務費
            total += this.orderContent.serviceFee
            // 刷卡手續費
            total += this.orderContent.creditCardSurcharge
            // 運費
            total += this.orderContent.shippingFee
        }
        return Math.round(total) < 0 ? 0 : Math.round(total)
    }

    // 訂單商品
    get orderGoods() {
        return this.orderContent.orderGoods
    }

    // 子單
    get isChildOrder() {
        return this.orderContent.isChildOrder
    }

    // 訂單商品數量
    get orderGoodsLength() {
        return this.orderGoods.length
    }

    // 訂單編號
    get displayOrderNumber() {
        // return 'B2402220006:dyPneauKXu1WZQ4ovDWaTW'
        return this.orderNumber.split(':')[0] || this.orderNumber
    }

    // Line 線上點餐產生訂單
    get isOnlineOrder() {
        // order number 開頭為 L
        return this.orderNumber.startsWith('L')
    }

    // 付款方式
    get paymentType() {
        return this._orderHistory.payment_method
    }

    // 呈現狀態 
    get displayOrderStatus() {
        // 如果是子單 顯示合併結帳
        if (this.isChildOrder) return '合併結帳'
        if (this.isUnprocessed) return '訂單已送出'
        if (this.isCancelledByUser) return '已取消'
        if (this.isProcessing) return '已接單'
        if (this.isPaid) return '已結帳'
        if (this.isCancelledByStore) return '已取消'
        return ''
    }

    // 付款方式(呈現用)
    get displayPaymentType() {
        switch (this.paymentType) {
            case 'cash':
                return '現金'
            case 'creditCard':
                return '信用卡'
            case 'linePay':
                return 'LINE Pay'
            case 'counterPay':
                return '櫃檯付款'
            default:
                return this.paymentType
        }
    }

    // 使用紅利積點 
    get bonusUsed() {
        const pointDecrease = this.orderContent?.orderCheckout?.pointDecrease || 0;
        return pointDecrease > 0 ? true : false
    }

    get displayConsumptionPattern() {
        if (this.consumptionPattern in consumptionPatternMap) {
            return consumptionPatternMap[this.consumptionPattern]
        }
        return ''
    }

    get isDineIn() {
        return this.consumptionPattern == 0
    }

    get isTakeOut() {
        return this.consumptionPattern == 1
    }

    get isPickUp() {
        return this.consumptionPattern == 2
    }

    get isDelivery() {
        return this.consumptionPattern == 3
    }

    get isRetailPickUp() {
        return this.consumptionPattern == 4
    }

    get isRetailDelivery() {
        return this.consumptionPattern == 5
    }

    get isPickupType() {
        return this.isPickUp || this.isRetailPickUp
    }

    get isDeliveryType() {
        return this.isDelivery || this.isRetailDelivery
    }

    get takeOrderTimeText() {
        if (this.isDeliveryType) return '送達時間'
        if (this.isRetailPickUp) return '取貨時間'
        if (this.isPickUp) return '取餐時間'
        return ''
    }

    get isUnprocessed() {
        return this.orderStatus == 0
    }

    get isCancelledByUser() {
        return this.orderStatus == 1
    }

    get isProcessing() {
        return this.orderStatus == 2
    }

    get isPaid() {
        return this.orderStatus == 3
    }

    get isCancelledByStore() {
        return this.orderStatus == 4
    }
}
